/** Shared row mapping for Backend dashboard + Assigned Complaints list (same API, same columns). */

import {
  formatComplaintStatusLabel,
  getComplaintStatusBadgeClass,
} from '../../utils/complaintStatus';

export const STATUS_BADGE_CLASSES = {
  Pending: 'bg-red-50 text-red-700',
  'Under Review': 'bg-orange-50 text-orange-700',
  Resolved: 'bg-emerald-50 text-emerald-700',
  Archived: 'bg-slate-100 text-slate-700',
  Transferred: 'bg-blue-50 text-blue-700',
};

export const getStatusClasses = (displayLabel) =>
  getComplaintStatusBadgeClass(displayLabel);

export { formatComplaintStatusLabel };

const formatShortDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const normalizeStorageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  return url.replace(/([^:])\/{2,}/g, '$1/');
};

export const formatReportedDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export function complaintTatDisplayValue(raw) {
  if (!raw || typeof raw !== 'object') return '—';
  const age = raw.complaint_created_hours_ago;
  if (age != null && String(age).trim() !== '') return String(age).trim();
  return '—';
}

export function createdByLabel(row) {
  const raw = row?.raised_by_name;
  if (raw != null && String(raw).trim() !== '') {
    const s = String(raw).trim();
    if (!/^\d+$/.test(s)) return s;
    return s;
  }
  const fallback = row?.created_by_name ?? row?.createdByName ?? row?.created_by;
  return fallback != null && String(fallback).trim() !== '' ? String(fallback) : '—';
}

function reportingSupervisorName(row) {
  const nameFromApi = row?.reporting_supervisor_name;
  if (nameFromApi != null && String(nameFromApi).trim() !== '') return String(nameFromApi).trim();
  const id = row?.reporting_supervisor_id ?? row?.account_id;
  if (id == null || id === '') return '—';
  return `ID ${id}`;
}

function locationLabel(row, locations = []) {
  const fromApi = row?.location_name;
  if (fromApi != null && String(fromApi).trim() !== '') return String(fromApi).trim();
  const locationId = row?.location_id;
  if (locationId != null && locationId !== '') {
    const loc = locations.find((l) => String(l.id) === String(locationId));
    if (loc?.name) return String(loc.name);
    return `Location ${locationId}`;
  }
  return '—';
}

/** Same columns as Super Admin complaints list. */
export const ADMIN_COMPLAINT_TABLE_HEADERS = [
  'Sr. No.',
  'Complaint ID',
  'Complaint Type',
  'Location',
  'Raised By',
  'Reporting Supervisor',
  'Reported Date',
  'TAT',
  'Status',
  'Action',
];

/**
 * Row mapping aligned with Super Admin `/dashboard/admin/complaints` table.
 * @param {Array} rawRows
 * @param {Array} [locations]
 */
export function mapAdminStyleComplaintRows(rawRows = [], locations = []) {
  const sorted = [...rawRows].sort((a, b) => {
    const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return db - da;
  });

  return sorted.map((row) => ({
    id: row?.id != null ? `${row.id}` : '—',
    numericId: row?.id,
    acknowledgementNumber: row?.acknowledgement_number ?? row?.id ?? '—',
    complaintType: row?.complaint_type || '—',
    location: locationLabel(row, locations),
    raisedBy: createdByLabel(row),
    managerOnDuty: reportingSupervisorName(row),
    reportedDate: formatReportedDateTime(row?.created_at),
    tat: complaintTatDisplayValue(row),
    status: formatComplaintStatusLabel(row?.status),
    priority: row?.work_priority_name || row?.priority_name || row?.priority || '—',
    raw: row,
  }));
}

/**
 * Same sort + fields as Assigned Complaints table.
 * @param {Array} rawRows
 */
export function mapAssignedRows(rawRows = []) {
  const sorted = [...rawRows].sort((a, b) => {
    const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return db - da;
  });

  return sorted.map((row) => ({
    id: row?.id != null ? `${row.id}` : '—',
    product: row?.product_name || row?.product || row?.machine_name || '—',
    department: row?.department_name || row?.department || '—',
    createdBy: createdByLabel(row),
    priority: row?.work_priority_name || row?.priority_name || row?.priority || '—',
    assignedTo: row?.assigned_to_name || row?.assigned_to || row?.assignedTo || '—',
    date: formatShortDate(row?.created_at),
    status: formatComplaintStatusLabel(row?.status),
    location: row?.location_name || '—',
    complaintType: row?.complaint_type || '—',
    reportedDate: formatReportedDateTime(row?.created_at),
    issueDescription: row?.issue_description || row?.remarks || '—',
    imageUrl: normalizeStorageUrl(row?.image_url),
    raw: row,
  }));
}
